import React, { useEffect, useState, useRef } from 'react';
import { LogoLink, LogoWrapper, LogoImage, Video } from './styled';

interface LogoData {
  src: string;
  alt: string;
}

interface AnimatedLogoData {
  src: string;
}

interface LogoComponentProps {
  logo: LogoData;
  animatedLogo: AnimatedLogoData;
  locale: string;
}

const LogoComponent: React.FC<LogoComponentProps> = ({
  logo,
  animatedLogo,
  locale,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasScrolled, setHasScrolled] = useState<boolean>(false);
  const [videoReady, setVideoReady] = useState<boolean>(false);
  const [showVideo, setShowVideo] = useState<boolean>(false);

  useEffect(() => {
    const handleScroll = (): void => {
      if (!hasScrolled) {
        setHasScrolled(true);
        window.removeEventListener('scroll', handleScroll);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return (): void => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasScrolled]);

  useEffect(() => {
    if (!hasScrolled) return (): void => {};

    const checkVideoReady = (): void => {
      const video = videoRef.current;
      if (video && video.readyState >= 2) {
        setVideoReady(true);
      }
    };

    const timeout = setTimeout(checkVideoReady, 500);

    return (): void => clearTimeout(timeout);
  }, [hasScrolled]);

  useEffect(() => {
    if (!videoReady) return (): void => {};

    let interval: ReturnType<typeof setInterval>;
    const video = videoRef.current;
    if (videoReady) {
      setShowVideo(true);
      if (video) {
        video.currentTime = 0;
        video.play();
      }

      interval = setInterval(() => {
        if (video) {
          setShowVideo(true);
          video.currentTime = 0;
          video.play();
        }
      }, 30000);
    }

    return (): void => clearInterval(interval);
  }, [videoReady]);

  const href: string = locale === 'en' ? '/' : `/${locale}/`;

  return (
    <LogoLink href={href}>
      <LogoWrapper>
        {(!hasScrolled || !showVideo) && (
          <LogoImage src={logo.src} alt={logo.alt} />
        )}

        {hasScrolled && (
          <Video
            ref={videoRef}
            src={animatedLogo.src}
            autoPlay
            muted
            playsInline
            disablePictureInPicture
            display={showVideo ? 'block' : 'none'}
          />
        )}
      </LogoWrapper>
    </LogoLink>
  );
};

export default LogoComponent;
